'use client';

import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { useAuth } from '@/lib/auth';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';
import Link from 'next/link';

interface TimelineEvent {
  timestamp: string;
  description: string;
}

interface ActionItem {
  description: string;
  owner: string;
  priority: 'High' | 'Medium' | 'Low';
  dueDate: string;
  status: 'Open' | 'In Progress' | 'Completed';
}

const SEVERITY_LEVELS = [
  { level: 'SEV-1', name: 'Critical', color: 'red', description: 'Complete outage, all users affected' },
  { level: 'SEV-2', name: 'High', color: 'orange', description: 'Major functionality degraded' },
  { level: 'SEV-3', name: 'Medium', color: 'yellow', description: 'Minor functionality impacted' },
  { level: 'SEV-4', name: 'Low', color: 'green', description: 'Minimal impact' },
];

export default function IncidentPostMortemPage() {
  // Incident Details
  const [incidentTitle, setIncidentTitle] = useState('');
  const [incidentDate, setIncidentDate] = useState('');
  const [severity, setSeverity] = useState('SEV-2');
  const [detectedTime, setDetectedTime] = useState('');
  const [resolvedTime, setResolvedTime] = useState('');

  // Impact Assessment
  const [usersAffected, setUsersAffected] = useState<number>(0);
  const [revenueLost, setRevenueLost] = useState<number>(0);
  const [servicesImpacted, setServicesImpacted] = useState<string>('');

  // Timeline
  const [timeline, setTimeline] = useState<TimelineEvent[]>([
    { timestamp: '', description: '' }
  ]);

  // Root Cause
  const [rootCause, setRootCause] = useState('');
  const [contributingFactors, setContributingFactors] = useState('');
  const [whyAnalysis, setWhyAnalysis] = useState(['', '', '', '', '']); // 5 Whys

  // Action Items
  const [actionItems, setActionItems] = useState<ActionItem[]>([
    { description: '', owner: '', priority: 'High', dueDate: '', status: 'Open' }
  ]);

  // Lessons Learned
  const [whatWentWell, setWhatWentWell] = useState('');
  const [whatWentWrong, setWhatWentWrong] = useState('');
  const [improvements, setImprovements] = useState('');

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'incident-postmortem',
    toolCategory: 'generator',
  });

  // Premium access control
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState<{ plan: 'free' | 'pro' | 'team'; status: string } | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);

  const TIER_HIERARCHY = {
    free: 0,
    pro: 1,
    team: 2,
  };

  useEffect(() => {
    async function fetchSubscription() {
      if (!isAuthenticated || !user) {
        setLoadingSubscription(false);
        return;
      }

      try {
        const response = await fetch('/api/secure/dashboard', {
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          setSubscription(data.data.subscription);
        }
      } catch (error) {
        console.error('Failed to fetch subscription:', error);
      } finally {
        setLoadingSubscription(false);
      }
    }

    fetchSubscription();
  }, [isAuthenticated, user]);

  const userTier = subscription?.plan || 'free';
  const hasProAccess = TIER_HIERARCHY[userTier] >= TIER_HIERARCHY.pro;

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Calculate MTTD and MTTR
  const calculateMetrics = () => {
    if (!detectedTime || !resolvedTime) return { mttd: 'N/A', mttr: 'N/A', totalDuration: 'N/A' };

    const detected = new Date(detectedTime).getTime();
    const resolved = new Date(resolvedTime).getTime();
    const started = incidentDate ? new Date(incidentDate).getTime() : detected;

    const mttd = Math.abs(detected - started) / (1000 * 60); // minutes
    const mttr = Math.abs(resolved - detected) / (1000 * 60); // minutes
    const total = Math.abs(resolved - started) / (1000 * 60); // minutes

    const formatTime = (mins: number) => {
      if (mins < 60) return `${mins.toFixed(0)} min`;
      const hours = Math.floor(mins / 60);
      const minutes = mins % 60;
      return `${hours}h ${minutes.toFixed(0)}m`;
    };

    return {
      mttd: formatTime(mttd),
      mttr: formatTime(mttr),
      totalDuration: formatTime(total),
    };
  };

  const addTimelineEvent = () => {
    setTimeline([...timeline, { timestamp: '', description: '' }]);
  };

  const updateTimelineEvent = (index: number, field: 'timestamp' | 'description', value: string) => {
    const updated = [...timeline];
    updated[index][field] = value;
    setTimeline(updated);
  };

  const removeTimelineEvent = (index: number) => {
    setTimeline(timeline.filter((_, i) => i !== index));
  };

  const addActionItem = () => {
    setActionItems([...actionItems, { description: '', owner: '', priority: 'Medium', dueDate: '', status: 'Open' }]);
  };

  const updateActionItem = <K extends keyof ActionItem>(index: number, field: K, value: ActionItem[K]) => {
    const updated = [...actionItems];
    updated[index][field] = value;
    setActionItems(updated);
  };

  const removeActionItem = (index: number) => {
    setActionItems(actionItems.filter((_, i) => i !== index));
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    if (!wb.Workbook) wb.Workbook = {};
    (wb.Workbook as any).CalcPr = { fullCalcOnLoad: true };

    const metrics = calculateMetrics();

    // Executive Summary
    const summaryData: any[] = [
      ['INCIDENT POST-MORTEM - EXECUTIVE SUMMARY'],
      [''],
      ['Incident Details'],
      ['Title:', incidentTitle || 'N/A'],
      ['Date:', incidentDate || 'N/A'],
      ['Severity:', severity],
      ['Detected:', detectedTime || 'N/A'],
      ['Resolved:', resolvedTime || 'N/A'],
      [''],
      ['Metrics'],
      ['MTTD (Mean Time To Detect):', metrics.mttd],
      ['MTTR (Mean Time To Resolve):', metrics.mttr],
      ['Total Duration:', metrics.totalDuration],
      [''],
      ['Impact'],
      ['Users Affected:', usersAffected.toLocaleString()],
      ['Revenue Lost:', `$${revenueLost.toLocaleString()}`],
      ['Services Impacted:', servicesImpacted || 'N/A'],
      [''],
      ['Root Cause'],
      [rootCause || 'N/A'],
      [''],
      ['Contributing Factors'],
      [contributingFactors || 'N/A'],
    ];

    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    wsSummary['!cols'] = [{ wch: 30 }, { wch: 60 }];
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

    // Timeline
    const timelineData: any[] = [
      ['INCIDENT TIMELINE'],
      [''],
      ['Timestamp', 'Event Description'],
    ];

    timeline.filter(e => e.timestamp || e.description).forEach(event => {
      timelineData.push([event.timestamp, event.description]);
    });

    const wsTimeline = XLSX.utils.aoa_to_sheet(timelineData);
    wsTimeline['!cols'] = [{ wch: 25 }, { wch: 80 }];
    XLSX.utils.book_append_sheet(wb, wsTimeline, 'Timeline');

    // 5 Whys Analysis
    const whyData: any[] = [
      ['5 WHYS ROOT CAUSE ANALYSIS'],
      [''],
      ['Question', 'Answer'],
    ];

    whyAnalysis.forEach((why, idx) => {
      if (why) {
        whyData.push([`Why ${idx + 1}?`, why]);
      }
    });

    const wsWhy = XLSX.utils.aoa_to_sheet(whyData);
    wsWhy['!cols'] = [{ wch: 15 }, { wch: 90 }];
    XLSX.utils.book_append_sheet(wb, wsWhy, '5 Whys');

    // Action Items
    const actionData: any[] = [
      ['ACTION ITEMS'],
      [''],
      ['Description', 'Owner', 'Priority', 'Due Date', 'Status'],
    ];

    actionItems.filter(a => a.description).forEach(action => {
      actionData.push([
        action.description,
        action.owner,
        action.priority,
        action.dueDate,
        action.status,
      ]);
    });

    const wsActions = XLSX.utils.aoa_to_sheet(actionData);
    wsActions['!cols'] = [{ wch: 50 }, { wch: 20 }, { wch: 10 }, { wch: 15 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, wsActions, 'Action Items');

    // Full Post-Mortem Document
    const documentData: any[] = [
      ['INCIDENT POST-MORTEM REPORT'],
      [''],
      ['=' + '='.repeat(80)],
      ['1. INCIDENT OVERVIEW'],
      ['=' + '='.repeat(80)],
      [''],
      ['Incident Title:', incidentTitle || '[Incident Title]'],
      ['Date:', incidentDate || '[Date]'],
      ['Severity:', severity],
      ['Incident Commander:', '[Name]'],
      ['Detected At:', detectedTime || '[Time]'],
      ['Resolved At:', resolvedTime || '[Time]'],
      [''],
      ['Metrics:'],
      [`- MTTD (Mean Time To Detect): ${metrics.mttd}`],
      [`- MTTR (Mean Time To Resolve): ${metrics.mttr}`],
      [`- Total Duration: ${metrics.totalDuration}`],
      [''],
      ['=' + '='.repeat(80)],
      ['2. IMPACT ASSESSMENT'],
      ['=' + '='.repeat(80)],
      [''],
      ['Users Affected:', usersAffected.toLocaleString() + ' users'],
      ['Revenue Impact:', `$${revenueLost.toLocaleString()}`],
      ['Services Impacted:', servicesImpacted || 'N/A'],
      [''],
      ['=' + '='.repeat(80)],
      ['3. TIMELINE OF EVENTS'],
      ['=' + '='.repeat(80)],
      [''],
    ];

    timeline.filter(e => e.timestamp || e.description).forEach(event => {
      documentData.push([`${event.timestamp} - ${event.description}`]);
    });

    documentData.push(
      [''],
      ['=' + '='.repeat(80)],
      ['4. ROOT CAUSE ANALYSIS'],
      ['=' + '='.repeat(80)],
      [''],
      ['Primary Root Cause:'],
      [rootCause || '[Root cause description]'],
      [''],
      ['Contributing Factors:'],
      [contributingFactors || '[Contributing factors]'],
      [''],
      ['5 Whys Analysis:'],
    );

    whyAnalysis.forEach((why, idx) => {
      if (why) {
        documentData.push([`Why ${idx + 1}: ${why}`]);
      }
    });

    documentData.push(
      [''],
      ['=' + '='.repeat(80)],
      ['5. WHAT WENT WELL'],
      ['=' + '='.repeat(80)],
      [''],
      [whatWentWell || '[Things that worked well during incident response]'],
      [''],
      ['=' + '='.repeat(80)],
      ['6. WHAT WENT WRONG'],
      ['=' + '='.repeat(80)],
      [''],
      [whatWentWrong || '[Things that did not work well]'],
      [''],
      ['=' + '='.repeat(80)],
      ['7. ACTION ITEMS'],
      ['=' + '='.repeat(80)],
      [''],
    );

    actionItems.filter(a => a.description).forEach((action, idx) => {
      documentData.push([
        `${idx + 1}. [${action.priority}] ${action.description}`,
        `   Owner: ${action.owner || '[Assign owner]'}`,
        `   Due Date: ${action.dueDate || '[Set due date]'}`,
        `   Status: ${action.status}`,
        '',
      ]);
    });

    documentData.push(
      ['=' + '='.repeat(80)],
      ['8. LESSONS LEARNED & IMPROVEMENTS'],
      ['=' + '='.repeat(80)],
      [''],
      [improvements || '[Key takeaways and systemic improvements]'],
      [''],
      ['=' + '='.repeat(80)],
      ['9. APPENDIX'],
      ['=' + '='.repeat(80)],
      [''],
      ['Links to:'],
      ['- Incident Slack channel: [URL]'],
      ['- Monitoring dashboards: [URL]'],
      ['- Related tickets: [URLs]'],
      ['- Code changes (PRs): [URLs]'],
      [''],
      ['Generated:', new Date().toLocaleString()],
      [''],
      ['Note: This is a blameless post-mortem. Focus on systems and processes, not individuals.'],
    );

    const wsDocument = XLSX.utils.aoa_to_sheet(documentData);
    wsDocument['!cols'] = [{ wch: 100 }];
    XLSX.utils.book_append_sheet(wb, wsDocument, 'Post-Mortem Document');

    const filename = `Incident_PostMortem_${incidentDate?.replace(/[^0-9]/g, '') || 'Draft'}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
    showToast('✓ Post-mortem exported successfully!', 'success');
  };

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    const metrics = calculateMetrics();

    // Generate post-mortem document as text
    const postMortemContent = [
      `INCIDENT POST-MORTEM REPORT`,
      ``,
      `Title: ${incidentTitle || 'Untitled Incident'}`,
      `Date: ${incidentDate || 'N/A'}`,
      `Severity: ${severity}`,
      ``,
      `METRICS:`,
      `- MTTD: ${metrics.mttd}`,
      `- MTTR: ${metrics.mttr}`,
      `- Total Duration: ${metrics.totalDuration}`,
      ``,
      `IMPACT:`,
      `- Users Affected: ${usersAffected.toLocaleString()}`,
      `- Revenue Lost: $${revenueLost.toLocaleString()}`,
      `- Services Impacted: ${servicesImpacted || 'N/A'}`,
      ``,
      `ROOT CAUSE:`,
      rootCause || 'N/A',
      ``,
      `CONTRIBUTING FACTORS:`,
      contributingFactors || 'N/A',
      ``,
      `5 WHYS ANALYSIS:`,
      ...whyAnalysis.filter(w => w).map((w, i) => `${i + 1}. ${w}`),
      ``,
      `TIMELINE:`,
      ...timeline.filter(t => t.timestamp || t.description).map(t => `- ${t.timestamp}: ${t.description}`),
      ``,
      `WHAT WENT WELL:`,
      whatWentWell || 'N/A',
      ``,
      `WHAT WENT WRONG:`,
      whatWentWrong || 'N/A',
      ``,
      `IMPROVEMENTS:`,
      improvements || 'N/A',
      ``,
      `ACTION ITEMS:`,
      ...actionItems.filter(a => a.description).map((a, i) => `${i + 1}. [${a.priority}] ${a.description} - Owner: ${a.owner || 'TBD'}, Due: ${a.dueDate || 'TBD'}, Status: ${a.status}`),
    ].join('\n');

    const assessment = await saveResult({
      name: incidentTitle || `Incident Post-Mortem - ${incidentDate || new Date().toLocaleDateString()}`,
      metadata: {
        incidentTitle: incidentTitle || 'Untitled Incident',
        incidentDate,
        severity,
        detectedTime,
        resolvedTime,
        mttd: metrics.mttd,
        mttr: metrics.mttr,
        totalDuration: metrics.totalDuration,
        usersAffected,
        revenueLost,
        servicesImpacted,
        rootCause,
        contributingFactors,
        whyAnalysis: whyAnalysis.filter(w => w),
        timeline: timeline.filter(t => t.timestamp || t.description),
        whatWentWell,
        whatWentWrong,
        improvements,
        actionItems: actionItems.filter(a => a.description),
        postMortemDocument: postMortemContent,
      },
    });

    if (assessment) {
      setSaved(true);
      showToast('Post-mortem saved to Command Center', 'success');
    }
  };

  const metrics = calculateMetrics();

  return (
    <div className="min-h-screen bg-background py-12">
      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg border-2 transform transition-all duration-300 ${
            toast.type === 'success'
              ? 'bg-green-900/90 border-green-500 text-green-100'
              : toast.type === 'error'
              ? 'bg-red-900/90 border-red-500 text-red-100'
              : 'bg-blue-900/90 border-blue-500 text-blue-100'
          }`}
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">
              {toast.type === 'success' ? '✓' : toast.type === 'error' ? '⚠' : 'ℹ'}
            </span>
            <span className="font-medium">{toast.message}</span>
            <button
              onClick={() => setToast(null)}
              className="ml-4 text-xl opacity-70 hover:opacity-100 transition-opacity"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-4">Incident Post-Mortem Generator</h1>
          <p className="text-foreground-secondary text-lg">
            Create comprehensive, blameless post-mortems with timeline analysis, root cause investigation, and actionable improvements.
          </p>
        </div>

        {/* Premium Feature Banner */}
        {!hasProAccess && (
          <div className="mb-6 bg-gradient-to-r from-accent-muted to-accent/5 border-2 border-accent/40 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-accent" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-foreground font-semibold">Excel Export is a Pro Feature</p>
                  <p className="text-foreground-secondary text-sm">Upgrade to Pro or Team to export comprehensive post-mortem documents with full timeline and action item tracking.</p>
                </div>
              </div>
              <Link
                href="/pricing"
                className="px-4 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors whitespace-nowrap"
              >
                Upgrade Now
              </Link>
            </div>
          </div>
        )}

        {/* Incident Details */}
        <div className="bg-surfaceer rounded-lg p-6 mb-8 border border-border">
          <h2 className="text-2xl font-bold text-accent mb-4">1. Incident Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-foreground text-sm font-medium mb-2">Incident Title</label>
              <input
                type="text"
                placeholder="e.g., Database Primary Failure During Peak Traffic"
                value={incidentTitle}
                onChange={(e) => setIncidentTitle(e.target.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded text-foreground placeholder-foreground-muted"
              />
            </div>

            <div>
              <label className="block text-foreground text-sm font-medium mb-2">Incident Date</label>
              <input
                type="date"
                value={incidentDate}
                onChange={(e) => setIncidentDate(e.target.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
              />
            </div>

            <div>
              <label className="block text-foreground text-sm font-medium mb-2">Severity Level</label>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
              >
                {SEVERITY_LEVELS.map(sev => (
                  <option key={sev.level} value={sev.level}>
                    {sev.level} - {sev.name}: {sev.description}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-foreground text-sm font-medium mb-2">Detected At</label>
              <input
                type="datetime-local"
                value={detectedTime}
                onChange={(e) => setDetectedTime(e.target.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
              />
            </div>

            <div>
              <label className="block text-foreground text-sm font-medium mb-2">Resolved At</label>
              <input
                type="datetime-local"
                value={resolvedTime}
                onChange={(e) => setResolvedTime(e.target.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
              />
            </div>
          </div>

          {/* Metrics Display */}
          {detectedTime && resolvedTime && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-background rounded-lg border border-border">
                <p className="text-foreground-secondary text-sm mb-1">MTTD</p>
                <p className="text-2xl font-bold text-accent">{metrics.mttd}</p>
                <p className="text-foreground-tertiary text-xs">Mean Time To Detect</p>
              </div>
              <div className="p-4 bg-background rounded-lg border border-border">
                <p className="text-foreground-secondary text-sm mb-1">MTTR</p>
                <p className="text-2xl font-bold text-accent">{metrics.mttr}</p>
                <p className="text-foreground-tertiary text-xs">Mean Time To Resolve</p>
              </div>
              <div className="p-4 bg-background rounded-lg border border-border">
                <p className="text-foreground-secondary text-sm mb-1">Total Duration</p>
                <p className="text-2xl font-bold text-accent">{metrics.totalDuration}</p>
                <p className="text-foreground-tertiary text-xs">Incident to Resolution</p>
              </div>
            </div>
          )}
        </div>

        {/* Impact Assessment */}
        <div className="bg-surfaceer rounded-lg p-6 mb-8 border border-border">
          <h2 className="text-2xl font-bold text-accent mb-4">2. Impact Assessment</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-foreground text-sm font-medium mb-2">Users Affected</label>
              <input
                type="number" inputMode="numeric"
                value={usersAffected}
                onChange={(e) => setUsersAffected(Number(e.target.value))}
                className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-foreground text-sm font-medium mb-2">Revenue Lost ($)</label>
              <input
                type="number" inputMode="numeric"
                value={revenueLost}
                onChange={(e) => setRevenueLost(Number(e.target.value))}
                className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-foreground text-sm font-medium mb-2">Services Impacted</label>
              <input
                type="text"
                value={servicesImpacted}
                onChange={(e) => setServicesImpacted(e.target.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                placeholder="e.g., API, Database, Frontend"
              />
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-surfaceer rounded-lg p-6 mb-8 border border-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-accent">3. Timeline of Events</h2>
            <button
              onClick={addTimelineEvent}
              className="px-4 py-2 bg-accent text-accent-foreground rounded-lg font-semibold hover:bg-accent-hover transition-colors text-sm"
            >
              + Add Event
            </button>
          </div>
          <div className="space-y-3">
            {timeline.map((event, idx) => (
              <div key={idx} className="flex gap-3 items-start">
                <input
                  type="datetime-local"
                  value={event.timestamp}
                  onChange={(e) => updateTimelineEvent(idx, 'timestamp', e.target.value)}
                  className="px-4 py-2 bg-background border border-border rounded text-foreground"
                />
                <input
                  type="text"
                  value={event.description}
                  onChange={(e) => updateTimelineEvent(idx, 'description', e.target.value)}
                  placeholder="Event description..."
                  className="flex-1 px-4 py-2 bg-background border border-border rounded text-foreground placeholder-foreground-muted"
                />
                {timeline.length > 1 && (
                  <button
                    onClick={() => removeTimelineEvent(idx)}
                    className="px-3 py-2 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition-colors"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Root Cause & 5 Whys */}
        <div className="bg-surfaceer rounded-lg p-6 mb-8 border border-border">
          <h2 className="text-2xl font-bold text-accent mb-4">4. Root Cause Analysis</h2>

          <div className="mb-6">
            <label className="block text-foreground text-sm font-medium mb-2">Primary Root Cause</label>
            <textarea
              value={rootCause}
              onChange={(e) => setRootCause(e.target.value)}
              rows={3}
              placeholder="Describe the primary root cause..."
              className="w-full px-4 py-2 bg-background border border-border rounded text-foreground placeholder-foreground-muted"
            />
          </div>

          <div className="mb-6">
            <label className="block text-foreground text-sm font-medium mb-2">Contributing Factors</label>
            <textarea
              value={contributingFactors}
              onChange={(e) => setContributingFactors(e.target.value)}
              rows={3}
              placeholder="List contributing factors..."
              className="w-full px-4 py-2 bg-background border border-border rounded text-foreground placeholder-foreground-muted"
            />
          </div>

          <div>
            <h3 className="text-lg font-semibold text-foreground mb-3">5 Whys Analysis</h3>
            <div className="space-y-3">
              {whyAnalysis.map((why, idx) => (
                <div key={idx}>
                  <label className="block text-foreground-secondary text-sm mb-1">Why {idx + 1}?</label>
                  <input
                    type="text"
                    value={why}
                    onChange={(e) => {
                      const updated = [...whyAnalysis];
                      updated[idx] = e.target.value;
                      setWhyAnalysis(updated);
                    }}
                    placeholder={idx === 0 ? "Why did the incident occur?" : "Why did the previous answer happen?"}
                    className="w-full px-4 py-2 bg-background border border-border rounded text-foreground placeholder-foreground-muted"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Action Items */}
        <div className="bg-surfaceer rounded-lg p-6 mb-8 border border-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-accent">5. Action Items</h2>
            <button
              onClick={addActionItem}
              className="px-4 py-2 bg-accent text-accent-foreground rounded-lg font-semibold hover:bg-accent-hover transition-colors text-sm"
            >
              + Add Action Item
            </button>
          </div>
          <div className="space-y-4">
            {actionItems.map((item, idx) => (
              <div key={idx} className="p-4 bg-background rounded-lg border border-border-muted">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                  <div className="md:col-span-2">
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => updateActionItem(idx, 'description', e.target.value)}
                      placeholder="Action item description..."
                      className="w-full px-4 py-2 bg-surfaceer border border-border rounded text-foreground placeholder-foreground-muted"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      value={item.owner}
                      onChange={(e) => updateActionItem(idx, 'owner', e.target.value)}
                      placeholder="Owner name..."
                      className="w-full px-4 py-2 bg-surfaceer border border-border rounded text-foreground placeholder-foreground-muted"
                    />
                  </div>
                  <div>
                    <input
                      type="date"
                      value={item.dueDate}
                      onChange={(e) => updateActionItem(idx, 'dueDate', e.target.value)}
                      className="w-full px-4 py-2 bg-surfaceer border border-border rounded text-foreground"
                    />
                  </div>
                  <div>
                    <select
                      value={item.priority}
                      onChange={(e) => updateActionItem(idx, 'priority', e.target.value as ActionItem['priority'])}
                      className="w-full px-4 py-2 bg-surfaceer border border-border rounded text-foreground"
                    >
                      <option value="High">High Priority</option>
                      <option value="Medium">Medium Priority</option>
                      <option value="Low">Low Priority</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={item.status}
                      onChange={(e) => updateActionItem(idx, 'status', e.target.value as ActionItem['status'])}
                      className="flex-1 px-4 py-2 bg-surfaceer border border-border rounded text-foreground"
                    >
                      <option value="Open">Open</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                    </select>
                    {actionItems.length > 1 && (
                      <button
                        onClick={() => removeActionItem(idx)}
                        className="px-3 py-2 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition-colors text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Lessons Learned */}
        <div className="bg-surfaceer rounded-lg p-6 mb-8 border border-border">
          <h2 className="text-2xl font-bold text-accent mb-4">6. Lessons Learned</h2>

          <div className="mb-4">
            <label className="block text-foreground text-sm font-medium mb-2">What Went Well</label>
            <textarea
              value={whatWentWell}
              onChange={(e) => setWhatWentWell(e.target.value)}
              rows={3}
              placeholder="What worked well during the incident response..."
              className="w-full px-4 py-2 bg-background border border-border rounded text-foreground placeholder-foreground-muted"
            />
          </div>

          <div className="mb-4">
            <label className="block text-foreground text-sm font-medium mb-2">What Went Wrong</label>
            <textarea
              value={whatWentWrong}
              onChange={(e) => setWhatWentWrong(e.target.value)}
              rows={3}
              placeholder="What didn't work well or could be improved..."
              className="w-full px-4 py-2 bg-background border border-border rounded text-foreground placeholder-foreground-muted"
            />
          </div>

          <div>
            <label className="block text-foreground text-sm font-medium mb-2">Systemic Improvements</label>
            <textarea
              value={improvements}
              onChange={(e) => setImprovements(e.target.value)}
              rows={3}
              placeholder="Long-term improvements to prevent similar incidents..."
              className="w-full px-4 py-2 bg-background border border-border rounded text-foreground placeholder-foreground-muted"
            />
          </div>
        </div>

        {/* Export */}
        <div className="bg-surfaceer rounded-lg p-6 mb-8 border border-border">
          <h2 className="text-2xl font-bold text-accent mb-4">7. Export Post-Mortem</h2>
          <div className="flex flex-wrap gap-4">
            {hasProAccess ? (
              <button
                onClick={exportToExcel}
                className="px-6 py-3 bg-accent text-accent-foreground rounded-lg font-semibold hover:bg-accent-hover transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export to Excel
              </button>
            ) : (
              <Link
                href="/pricing"
                className="px-6 py-3 bg-accent-muted border-2 border-accent text-accent rounded-lg font-semibold hover:bg-accent/30 transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                Upgrade to Export
              </Link>
            )}
            {commandCenterEnabled && !saved && (
              <button
                onClick={handleSaveToCommandCenter}
                disabled={saving}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? 'Saving...' : 'Save to Command Center'}
              </button>
            )}
            {saved && (
              <div className="px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Saved to Command Center
              </div>
            )}
            {saveError && (
              <div className="px-6 py-3 bg-red-600/20 text-red-400 rounded-lg text-sm">
                {saveError}
              </div>
            )}
          </div>
          <p className="text-foreground-tertiary text-sm mt-3">
            Export includes: Executive summary, timeline, 5 Whys analysis, action items tracker, and full blameless post-mortem document.
          </p>
        </div>

        {/* How to Use */}
        <div className="bg-surfaceer rounded-lg p-6 border border-border">
          <h3 className="text-xl font-bold text-accent mb-4">How to Use</h3>
          <ol className="list-decimal list-inside space-y-2 text-foreground-secondary">
            <li><strong>Fill Incident Details</strong>: Record when the incident occurred, was detected, and resolved</li>
            <li><strong>Assess Impact</strong>: Document users affected, revenue impact, and services impacted</li>
            <li><strong>Build Timeline</strong>: Add chronological events from incident start to resolution</li>
            <li><strong>Analyze Root Cause</strong>: Use 5 Whys methodology to identify the true root cause</li>
            <li><strong>Create Action Items</strong>: Define preventive measures with owners and due dates</li>
            <li><strong>Capture Lessons</strong>: Document what went well, what went wrong, and systemic improvements</li>
            <li><strong>Export</strong>: Generate a comprehensive, blameless post-mortem document to share with stakeholders</li>
          </ol>

          <div className="mt-6 p-4 bg-accent-muted border border-border rounded">
            <p className="text-foreground font-semibold mb-2">💡 Blameless Culture:</p>
            <p className="text-foreground-secondary text-sm">
              Focus on systems and processes, not individuals. Post-mortems should identify how to improve systems to prevent future incidents, not assign blame.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
